import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const getPhoneDisplay = (pn) => {
  if (!pn) return "";
  if (typeof pn === "string") return pn;
  return pn.sanitized_number || pn.raw_number || pn.number || pn.phone_number || "";
};

const TIER_STYLE = {
  Hot: { bg: "#fee2e2", color: "#dc2626", dot: "#ef4444" },
  Warm: { bg: "#fef3c7", color: "#b45309", dot: "#f59e0b" },
  Cold: { bg: "#e2e8f0", color: "#475569", dot: "#94a3b8" },
};

export const TierBadge = ({ tier, score, reasons }) => {
  const t = TIER_STYLE[tier] || TIER_STYLE.Cold;
  return (
    <span
      title={reasons && reasons.length ? reasons.join(" · ") : tier}
      data-testid={`tier-badge-${tier}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: "5px",
        background: t.bg, color: t.color, fontSize: "11px", fontWeight: 700,
        padding: "3px 9px", borderRadius: "12px", whiteSpace: "nowrap", cursor: "default",
      }}
    >
      <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: t.dot }} />
      {tier}{typeof score === "number" ? ` · ${score}` : ""}
    </span>
  );
};

const getAllEmails = (lead) => {
  const emails = [];
  const seen = new Set();
  const push = (val, status, type) => {
    if (!val || seen.has(val.toLowerCase())) return;
    seen.add(val.toLowerCase());
    emails.push({ email: val, status, type });
  };
  if (lead?.email) push(lead.email, lead.email_status, "Work");
  (lead?.personal_emails || []).forEach((e) => {
    const val = typeof e === "string" ? e : e?.email || e?.value;
    const status = typeof e === "object" ? e?.email_status || e?.status : undefined;
    push(val, status, "Personal");
  });
  return emails;
};

const getAllPhones = (lead) => {
  const phones = [];
  const seen = new Set();
  (lead?.phone_numbers || []).forEach((pn) => {
    const val = getPhoneDisplay(pn);
    if (!val || seen.has(val)) return;
    seen.add(val);
    phones.push({
      number: val,
      type: (typeof pn === "object" ? pn.type_cd || pn.type : null) || null,
      status: (typeof pn === "object" ? pn.status_cd || pn.status : null) || null,
    });
  });
  if (phones.length === 0 && lead?.phone && !seen.has(lead.phone)) {
    phones.push({ number: lead.phone, type: null, status: null });
  }
  return phones;
};

function MyLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCredits, setTotalCredits] = useState(0);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    try {
      const res = await axios.get(`${API}/saved-leads`);
      setLeads(res.data.leads || []);
      setTotalCredits(res.data.total_credits_spent || 0);
    } catch (error) {
      console.error("Error loading saved leads:", error);
      toast.error("Failed to load your leads");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (lead, e) => {
    e.stopPropagation();
    if (!window.confirm("Remove this lead from your library? This does not refund credits.")) return;
    try {
      await axios.delete(`${API}/saved-leads/${lead.person_id}`);
      setLeads((prev) => prev.filter((l) => l.person_id !== lead.person_id));
      toast.success("Lead removed");
    } catch (error) {
      toast.error("Failed to remove lead");
    }
  };

  const exportCSV = () => {
    if (filtered.length === 0) {
      toast.info("No leads to export");
      return;
    }
    const headers = [
      "First Name", "Last Name", "Title", "Seniority", "Company", "Domain", "Industry",
      "Employees", "Revenue", "Email", "Email Status", "Personal Emails", "Phones",
      "LinkedIn", "City", "State", "Country", "Credits Spent",
    ];
    const rows = filtered.map((l) => {
      const emails = getAllEmails(l);
      const phones = getAllPhones(l);
      const work = emails.find((e) => e.type === "Work");
      const personal = emails.filter((e) => e.type === "Personal").map((e) => e.email).join("; ");
      return [
        l.first_name, l.last_name, l.job_title, l.seniority, l.company, l.company_domain,
        l.company_industry, l.company_employee_count, l.company_revenue,
        work?.email || "", work?.status || "", personal,
        phones.map((p) => p.number).join("; "),
        l.linkedin_url, l.city, l.state, l.country, l.credits_spent || 0,
      ];
    });
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `my-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} leads`);
  };

  const filtered = leads
    .filter((l) => {
      if (tierFilter !== "all" && (l.tier || "Cold") !== tierFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return [l.first_name, l.last_name, l.company, l.email, l.job_title]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    })
    .sort((a, b) => (b.score || 0) - (a.score || 0));

  const openModal = (lead) => {
    setSelected(lead);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading your leads...</p>
      </div>
    );
  }

  const labelStyle = { color: "#64748b" };
  const rowStyle = { display: "grid", gridTemplateColumns: "130px 1fr", gap: "8px", fontSize: "14px" };

  return (
    <div data-testid="my-leads-page">
      <div className="page-header">
        <h1 className="page-title">My Leads</h1>
        <p className="page-description">
          Every contact you've unlocked (email, phone, or full profile) is saved here automatically.
        </p>
      </div>

      <div className="content-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: "13px", color: "#64748b" }} data-testid="my-leads-count">
              {leads.length} lead{leads.length === 1 ? "" : "s"} saved
            </span>
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                padding: "2px 10px",
                borderRadius: "12px",
                background: "#eef2ff",
                color: "#4338ca",
              }}
            >
              {totalCredits} credits spent
            </span>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              data-testid="my-leads-tier-filter"
              style={{ padding: "8px 12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px", background: "#fff", cursor: "pointer" }}
            >
              <option value="all">All leads</option>
              <option value="Hot">🔥 Hot only</option>
              <option value="Warm">Warm only</option>
              <option value="Cold">Cold only</option>
            </select>
            <input
              type="text"
              placeholder="Search name, company, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="my-leads-search"
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                fontSize: "13px",
                minWidth: "220px",
              }}
            />
            <Button
              onClick={exportCSV}
              className="btn btn-secondary"
              data-testid="export-csv-btn"
              style={{ fontSize: "13px" }}
            >
              ⬇ Export CSV
            </Button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📇</div>
            <div className="empty-state-title">No leads yet</div>
            <div className="empty-state-description">
              Head to Discover, unlock a contact's email/phone or fetch their full profile, and it will
              appear here automatically.
            </div>
          </div>
        ) : (
          <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table className="data-table" data-testid="my-leads-table" style={{ minWidth: "850px" }}>
              <thead>
                <tr>
                  <th>Score</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Title</th>
                  <th>Location</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((lead, index) => {
                  const emails = getAllEmails(lead);
                  const phones = getAllPhones(lead);
                  return (
                    <tr key={lead.person_id} data-testid={`my-lead-row-${index}`}>
                      <td>
                        <TierBadge tier={lead.tier} score={lead.score} reasons={lead.score_reasons} />
                      </td>
                      <td>
                        <strong style={{ fontSize: "13px" }}>
                          {lead.first_name} {lead.last_name}
                        </strong>
                        {lead.seniority && (
                          <div style={{ fontSize: "11px", color: "#64748b" }}>{lead.seniority}</div>
                        )}
                      </td>
                      <td style={{ fontSize: "13px" }}>{lead.company || "-"}</td>
                      <td style={{ fontSize: "12px" }}>
                        {emails.length > 0 ? (
                          <div>
                            <a href={`mailto:${emails[0].email}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                              {emails[0].email}
                            </a>
                            {emails.length > 1 && (
                              <button
                                onClick={() => openModal(lead)}
                                style={{
                                  display: "block",
                                  marginTop: "2px",
                                  padding: "1px 6px",
                                  borderRadius: "8px",
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  background: "#d1fae5",
                                  color: "#047857",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              >
                                +{emails.length - 1} more
                              </button>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>-</span>
                        )}
                      </td>
                      <td style={{ fontSize: "12px" }}>
                        {phones.length > 0 ? (
                          <div>
                            <a href={`tel:${phones[0].number}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                              {phones[0].number}
                            </a>
                            {phones.length > 1 && (
                              <button
                                onClick={() => openModal(lead)}
                                style={{
                                  display: "block",
                                  marginTop: "2px",
                                  padding: "1px 6px",
                                  borderRadius: "8px",
                                  fontSize: "10px",
                                  fontWeight: 600,
                                  background: "#dbeafe",
                                  color: "#1d4ed8",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                              >
                                +{phones.length - 1} more
                              </button>
                            )}
                          </div>
                        ) : lead.phone_pending ? (
                          <span style={{ fontSize: "11px", color: "#b45309" }}>⏳ Pending</span>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>-</span>
                        )}
                      </td>
                      <td style={{ fontSize: "12px" }}>{lead.job_title || "-"}</td>
                      <td style={{ fontSize: "12px" }}>
                        {[lead.city, lead.state, lead.country].filter(Boolean).join(", ") || "-"}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <Button
                            onClick={() => openModal(lead)}
                            className="btn btn-secondary"
                            style={{ padding: "4px 8px", fontSize: "11px" }}
                            data-testid={`my-lead-view-${index}`}
                          >
                            View
                          </Button>
                          <Button
                            onClick={(e) => handleDelete(lead, e)}
                            className="btn btn-secondary"
                            style={{ padding: "4px 8px", fontSize: "11px", color: "#dc2626" }}
                            data-testid={`my-lead-delete-${index}`}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Lead Detail Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl" style={{ maxHeight: "85vh", overflowY: "auto" }}>
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
          </DialogHeader>

          {selected &&
            (() => {
              const lead = selected;
              const emails = getAllEmails(lead);
              const phones = getAllPhones(lead);
              return (
                <div style={{ padding: "16px 0" }} data-testid="my-lead-modal">
                  <div style={{ marginBottom: "20px", display: "flex", gap: "14px", alignItems: "flex-start" }}>
                    {lead.photo_url && (
                      <img
                        src={lead.photo_url}
                        alt={lead.first_name}
                        style={{ width: "56px", height: "56px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{ fontSize: "18px", fontWeight: "700", marginBottom: "4px" }}>
                        {lead.first_name} {lead.last_name}
                      </h3>
                      <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
                        {lead.job_title || "No job title"}
                        {lead.company && ` at ${lead.company}`}
                      </p>
                      {lead.headline && (
                        <p style={{ color: "#475569", fontSize: "12px", marginTop: "6px", lineHeight: 1.4 }}>
                          {lead.headline}
                        </p>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "grid", gap: "12px" }}>
                    <div style={rowStyle}>
                      <span style={labelStyle}>Emails:</span>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }} data-testid="my-modal-emails">
                        {emails.length > 0 ? (
                          emails.map((em, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                              <a href={`mailto:${em.email}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                                {em.email}
                              </a>
                              {em.type && (
                                <span style={{ padding: "1px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: 600, background: "#eef2ff", color: "#4338ca" }}>
                                  {em.type}
                                </span>
                              )}
                              {em.status && (
                                <span style={{ padding: "1px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: 600, background: em.status === "verified" ? "#d1fae5" : "#fef3c7", color: em.status === "verified" ? "#047857" : "#b45309" }}>
                                  {em.status}
                                </span>
                              )}
                            </div>
                          ))
                        ) : (
                          <span style={{ color: "#94a3b8" }}>None</span>
                        )}
                      </div>
                    </div>

                    <div style={rowStyle}>
                      <span style={labelStyle}>Phones:</span>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }} data-testid="my-modal-phones">
                        {phones.length > 0 ? (
                          phones.map((ph, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                              <a href={`tel:${ph.number}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                                {ph.number}
                              </a>
                              {ph.type && (
                                <span style={{ padding: "1px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: 600, background: "#dbeafe", color: "#1d4ed8" }}>
                                  {ph.type}
                                </span>
                              )}
                            </div>
                          ))
                        ) : lead.phone_pending ? (
                          <span style={{ color: "#b45309" }}>⏳ Enrichment in progress...</span>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>None</span>
                        )}
                      </div>
                    </div>

                    <div style={rowStyle}>
                      <span style={labelStyle}>Social:</span>
                      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                        {lead.linkedin_url && (
                          <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>
                            LinkedIn
                          </a>
                        )}
                        {lead.twitter_url && (
                          <a href={lead.twitter_url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>
                            Twitter/X
                          </a>
                        )}
                        {lead.facebook_url && (
                          <a href={lead.facebook_url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>
                            Facebook
                          </a>
                        )}
                        {!lead.linkedin_url && !lead.twitter_url && !lead.facebook_url && (
                          <span style={{ color: "#94a3b8" }}>-</span>
                        )}
                      </div>
                    </div>

                    <div style={rowStyle}>
                      <span style={labelStyle}>Location:</span>
                      <span>
                        {lead.formatted_address ||
                          [lead.city, lead.state, lead.country].filter(Boolean).join(", ") ||
                          "-"}
                      </span>
                    </div>

                    <hr style={{ margin: "8px 0", borderColor: "#e2e8f0" }} />

                    <div style={rowStyle}>
                      <span style={labelStyle}>Company:</span>
                      <span>{lead.company || "-"}</span>
                    </div>
                    <div style={rowStyle}>
                      <span style={labelStyle}>Domain:</span>
                      <span>{lead.company_domain || "-"}</span>
                    </div>
                    <div style={rowStyle}>
                      <span style={labelStyle}>Industry:</span>
                      <span>{lead.company_industry || "-"}</span>
                    </div>
                    <div style={rowStyle}>
                      <span style={labelStyle}>Employees:</span>
                      <span>{lead.company_employee_count || "-"}</span>
                    </div>
                    <div style={rowStyle}>
                      <span style={labelStyle}>Revenue:</span>
                      <span>{lead.company_revenue || "-"}</span>
                    </div>
                    <div style={rowStyle}>
                      <span style={labelStyle}>Website:</span>
                      <span>
                        {lead.company_website ? (
                          <a href={lead.company_website} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>
                            {lead.company_website}
                          </a>
                        ) : (
                          "-"
                        )}
                      </span>
                    </div>

                    {lead.employment_history && lead.employment_history.length > 0 && (
                      <>
                        <hr style={{ margin: "8px 0", borderColor: "#e2e8f0" }} />
                        <div>
                          <span style={{ ...labelStyle, fontWeight: 600, display: "block", marginBottom: "8px" }}>
                            Employment History:
                          </span>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }} data-testid="my-modal-employment">
                            {lead.employment_history.map((job, i) => (
                              <div key={i} style={{ borderLeft: "2px solid #e2e8f0", paddingLeft: "10px" }}>
                                <div style={{ fontSize: "13px", fontWeight: 600 }}>
                                  {job.title || "Role"}
                                  {job.current && (
                                    <span style={{ marginLeft: "6px", padding: "1px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: 600, background: "#d1fae5", color: "#047857" }}>
                                      Current
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: "12px", color: "#64748b" }}>{job.organization_name || "-"}</div>
                                <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                                  {(job.start_date || "").slice(0, 7) || "?"} —{" "}
                                  {job.current ? "Present" : (job.end_date || "").slice(0, 7) || "?"}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default MyLeads;
